import { z } from 'zod';
import { transformAndFilterValidItems } from '../../../common/tools/transformUtils';

/**
 * Schema for validating SubnationalRegionType objects
 */
export const SubnationalRegionTypeSchema = z
    .object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
        selectionPrompt: z.string(),
    })
    .describe('SubnationalRegionTypeSchema');

/**
 * Schema for validating SubnationalRegion objects
 */
export const SubnationalRegionSchema = z
    .object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
        // HubspotYageoContactUsSubnationalRegion brick (if not attached - nulls or `enabled` is false,
        // subnational region will not be shown in the form)
        hubspotYageoContactUs_enabled: z
            .boolean()
            .nullable()
            .transform((v) => v ?? false),
        hubspotYageoContactUs_fieldValue: z.string().nullable(),
    })
    .superRefine((data, ctx) => {
        if (
            data.hubspotYageoContactUs_enabled === true &&
            (data.hubspotYageoContactUs_fieldValue == null ||
                data.hubspotYageoContactUs_fieldValue.trim() === '')
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    '`hubspotYageoContactUs_fieldValue` is required when `hubspotYageoContactUs_enabled` is true.',
                path: ['hubspotYageoContactUs_fieldValue'],
            });
        }
    })
    .describe('SubnationalRegionSchema');

/**
 * Schema for validating Country objects
 */
export const CountrySchema = z
    .object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
        // HubspotYageoContactUsCountry brick (if not attacched - nulls or `enabled` is false, country will
        // not be shown in the form)
        hubspotYageoContactUs_enabled: z
            .boolean()
            .nullable()
            .transform((v) => v ?? false),
        hubspotYageoContactUs_fieldValue: z.string().nullable(),
        hubspotYageoContactUs_subregionFieldName: z.string().nullable(),
        subnationalRegionType: SubnationalRegionTypeSchema.nullable(),
        subnationalRegions: z
            .array(z.unknown())
            .transform((subnations) =>
                transformAndFilterValidItems(SubnationalRegionSchema, subnations)
            ),
    })
    .superRefine((data, ctx) => {
        if (
            data.hubspotYageoContactUs_enabled === true &&
            (data.hubspotYageoContactUs_fieldValue == null ||
                data.hubspotYageoContactUs_fieldValue.trim() === '')
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    '`hubspotYageoContactUs_fieldValue` is required when `hubspotYageoContactUs_enabled` is true.',
                path: ['hubspotYageoContactUs_fieldValue'],
            });
        }
    })
    .describe('CountrySchema');

/**
 * Schema for validating GeographicRegion objects
 */
export const GeographicRegionSchema = z
    .object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
        countries: z
            .array(z.unknown())
            .transform((countries) => transformAndFilterValidItems(CountrySchema, countries)),
    })
    .describe('GeographicRegionSchema');

/**
 * Main schema to validate the entire query response
 */
export const GetGeographicRegionListingSchema = z
    .object({
        getObjectFolder: z.object({
            children: z
                .array(z.unknown())
                .transform((regions) =>
                    transformAndFilterValidItems(GeographicRegionSchema, regions)
                ),
        }),
    })
    .describe('GetGeographicRegionListingSchema');
